'use client'

import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from 'react'

import { HeaderActionNav } from '@/app/components/HeaderActionNav'
import { Icon } from '@/app/components/Icon'
import { Modal } from '@/app/components/Modal'
import { Navigation } from '@/app/components/Navigation'
import { PavedLogo } from '@/app/components/PavedLogo'
import { useUser } from '@/app/contexts/UserContext'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { BLOG_INDEX_PATH, isBlogPath } from '@/lib/isBlogPath'
import { NavQueryResult } from '@/sanity.types'

export const HEADER_HEIGHT = 88

interface HeaderProps {
  className?: string
  mode?: 'light' | 'dark'
  navGuest: NavQueryResult[number] | null
  navGuestCta: NavQueryResult[number] | null
  navAdvertiser: NavQueryResult[number] | null
  navPublisher: NavQueryResult[number] | null
}

// Navigation wrapper that checks for nav=false in URL
function NavigationWithParams({
  nav,
  classNames,
  tracking,
  collapsible,
  collapseMode,
  ignoreButtonStyles,
  includeItemDescription,
  themeMode,
  variantMapping,
}: {
  nav: NavQueryResult[number] | null
  classNames: any
  tracking: any
  collapsible?: boolean
  collapseMode?: 'accordion'
  ignoreButtonStyles?: boolean
  includeItemDescription?: boolean
  themeMode?: 'light' | 'dark'
  variantMapping?: Record<string, string>
}) {
  const searchParams = useSearchParams()
  const hideNav = searchParams.get('nav') === 'false'

  if (!nav || hideNav) return null

  return (
    <Navigation
      nav={nav}
      tracking={tracking}
      classNames={classNames}
      collapsible={collapsible}
      collapseMode={collapseMode}
      ignoreButtonStyles={ignoreButtonStyles}
      includeItemDescription={includeItemDescription}
      themeMode={themeMode}
      variantMapping={variantMapping}
    />
  )
}

export const Header = function ({
  className,
  mode = 'light',
  navGuest,
  navGuestCta,
  navAdvertiser,
  navPublisher,
}: HeaderProps) {
  const { user } = useUser()
  const [menuVisible, setMenuVisible] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const isLightMode = mode === 'light' || isScrolled

  // Variant mapping for light mode (dark variants on light background)
  const desktopVariantMapping: Record<string, string> = isLightMode
    ? {
        navbarLink: 'navbarLinkDark',
        navbarCtaDark: 'navbarCta',
      }
    : {
        navbarLinkDark: 'navbarLink',
        navbarCta: 'navbarCtaDark',
      }

  // Determine which navigation to use based on user type
  const nav =
    user?.user_type === 'advertiser'
      ? navAdvertiser
      : user?.user_type === 'publisher'
        ? navPublisher
        : navGuest

  const navCta = user ? null : navGuestCta

  // Determine logo link based on user type
  const logoHref =
    user?.user_type === 'publisher'
      ? 'https://www.paved.com/publisher/campaigns'
      : user?.user_type === 'advertiser'
        ? 'https://www.paved.com/marketplace'
        : '/'

  // Close mobile menu when pathname changes
  useEffect(() => {
    setMenuVisible(false)
  }, [pathname])

  // Detect scroll position for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }

    // Set initial state
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinkClassnames =
    'text-paved-white font-book hover:text-paved-blue-200 active:text-paved-white hover:active:text-paved-blue-200 focus:text-paved-white focus:underline focus:outline-none transition-all duration-300'

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 h-[88px] px-3xl py-2xl lg:px-8xl',
          'transition-shadow [transition-duration:var(--default-transition-duration)]',
          isScrolled && '[box-shadow:var(--nav-shadow)]',
          className,
        )}
      >
        {/* Mobile menu overlay */}
        <Modal
          classNames={{
            root: 'bg-white-default p-md h-mobile-menu',
            inner: 'bg-neutral-50 rounded-xs',
          }}
          direction="top"
          height="h-full"
          topOffset={menuVisible ? HEADER_HEIGHT : 0}
          isOpen={menuVisible}
          onClose={() => setMenuVisible(false)}
        >
          <div className="h-full flex flex-col gap-2xl justify-between">
            <Suspense fallback={null}>
              <NavigationWithParams
                nav={nav}
                tracking={{ location: TRACKING_LOCATIONS.MOBILE_MENU }}
                classNames={{
                  root: 'flex flex-col pb-10xl',
                  list: 'flex flex-col items-start',
                  link: 'flex flex-col gap-0 p-lg',
                  item: 'w-full size-body-sm text-white-950 bg-transparent hover:bg-neon-100 active:bg-neon-200 transition-colors duration-200 cursor-pointer',
                  itemOpened: 'bg-neon-200 hover:bg-neon-200',
                  subItem:
                    'text-white-950 bg-transparent hover:bg-neon-100 active:bg-neon-200 transition-colors duration-200 cursor-pointer',
                  subItemDescription: 'size-body-xs text-paved-gray-blue-500',
                  subLink: 'flex flex-col gap-0 py-lg px-xl',
                  subList: 'bg-white-default px-[5px] shadow-[inset_0_0_0_5px_transparent]',
                  subListOpened: 'shadow-[inset_0_0_0_5px_var(--color-neon-100)] p-[5px]',
                  collapseButton: 'relative w-full text-left cursor-pointer',
                  collapseIndicator:
                    'text-white-950 absolute right-0 top-1/2 -translate-y-1/2 mr-lg',
                }}
                collapsible
                collapseMode="accordion"
                ignoreButtonStyles
              />
            </Suspense>
            <HeaderActionNav
              navCta={navCta}
              variantMapping={{
                navbarLink: 'navbarLinkDark',
                navbarCtaDark: 'navbarCta',
              }}
              classNames={{
                root: 'flex flex-col absolute w-[calc(100%-24px)] py-xl px-2xl bottom-[12px] left-[12px] bg-white-default rounded-b-xs',
                list: 'flex flex-row gap-xl items-center w-full justify-center',
                item: '',
                user: 'size-header-h4',
                userMenu: 'bottom-full top-auto left-0 right-0 w-full',
                link: cn(navLinkClassnames, 'size-header-h5'),
              }}
            />
          </div>
        </Modal>
        {/* Background layer at z-50 to stay above modal */}
        <div
          className={cn(
            'absolute inset-0 z-50 pointer-events-none',
            'transition-colors [transition-duration:var(--default-transition-duration)]',
            isLightMode ? 'bg-white-default' : 'bg-green-700',
          )}
          aria-hidden="true"
        />
        <div
          className={cn(
            'relative z-50 flex items-center justify-between lg:gap-8xl xl:grid xl:grid-cols-[1fr_auto_1fr] xl:items-center w-full min-h-[40px]',
          )}
        >
          {/* Logo - Left aligned; blog pages add a "Blog" label that links to the blog index */}
          <div className="flex items-center gap-sm xl:justify-self-start">
            <Link className="flex items-center gap-2" href={logoHref} aria-label="Paved">
              <PavedLogo
                className={cn(
                  'w-[100px] h-[36px]',
                  isLightMode ? 'text-green-900' : 'text-green-100',
                )}
              />
            </Link>
            {isBlogPath(pathname) && (
              <Link
                href={BLOG_INDEX_PATH}
                aria-current={pathname === BLOG_INDEX_PATH ? 'page' : undefined}
                className={cn(
                  'pr-lg font-body text-(length:--font-size-xl) leading-none font-normal tracking-[-0.02em] transition-colors [transition-duration:var(--default-transition-duration)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current',
                  isLightMode ? 'text-green-900' : 'text-green-100',
                )}
              >
                Blog
              </Link>
            )}
          </div>

          {/* Mobile hamburger menu */}
          <button
            type="button"
            onClick={() => setMenuVisible(!menuVisible)}
            aria-label="Toggle menu"
            aria-expanded={menuVisible}
            className={cn(
              'lg:hidden flex items-center justify-center cursor-pointer h-[40px] w-[40px] rounded-xs ml-auto',
              isLightMode ? 'bg-neon-200' : 'bg-green-600',
            )}
          >
            <Icon
              name={menuVisible ? 'faClose' : 'faBars'}
              provider="fontawesome"
              style="regular"
              size={22}
              className={cn(isLightMode ? 'text-neutral-950' : 'text-neutral-50')}
            />
          </button>

          {/* Main navigation - Fluid on lg, Centered on xl */}
          <div className="hidden lg:flex xl:justify-self-center">
            <Suspense fallback={null}>
              <NavigationWithParams
                nav={nav}
                tracking={{ location: TRACKING_LOCATIONS.HEADER }}
                classNames={{
                  root: 'flex',
                  list: 'flex flex-row gap-sm items-center',
                  link: 'flex flex-col gap-0 py-xl whitespace-nowrap cursor-pointer hover:text-paved-blue-200 active:underline active:text-paved-white hover:active:text-paved-blue-200 focus:outline-none focus-visible:text-paved-blue-200 focus-visible:underline',
                  collapseButton: 'relative w-full text-left cursor-pointer',
                  collapseIndicator: 'ml-sm',
                  item: 'group w-full size-body-xs text-paved-white',
                  subList:
                    'absolute top-full left-0 pt-lg py-xl w-max overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-300 grid-rows-[1fr]',
                  subItem:
                    'max-w-[400px] first:rounded-t-xs last:rounded-b-xs overflow-hidden w-full',
                  subLink:
                    'block p-lg size-body-sm text-white-950 bg-neutral-50 hover:bg-neon-100 active:bg-neon-200 transition-colors duration-200 cursor-pointer',
                }}
                collapsible
                themeMode={mode}
                variantMapping={desktopVariantMapping}
              />
            </Suspense>
          </div>

          {/* CTA navigation - Fluid on lg, Right aligned on xl */}
          <div className="hidden lg:flex xl:justify-self-end">
            <HeaderActionNav
              navCta={navCta}
              themeMode={mode}
              variantMapping={desktopVariantMapping}
              classNames={{
                root: 'flex',
                item: 'flex items-center',
                link: navLinkClassnames,
                user: 'size-header-h5',
              }}
            />
          </div>
        </div>
      </header>
    </>
  )
}
